import React from 'react';
import { getActions, getBlocks, getImageUrl } from '../shared/templateSectionUtils.jsx';

const themeOf = (theme = {}) => ({
  primary: '#07111f',
  accent: '#0b0f16',
  background: '#e6e8eb',
  surface: '#ffffff',
  color: '#0b0f16',
  mutedColor: '#5f6670',
  headingFontFamily: 'Inter, Arial, sans-serif',
  fontFamily: 'Inter, Arial, sans-serif',
  pageWidth: '1120px',
  sectionSpacing: '72px 24px',
  buttonRadius: '999px',
  buttonHeight: '42px',
  cardShadow: '0 18px 42px rgba(24, 34, 46, 0.10)',
  imageRadius: '24px',
  ...theme
});

const pick = (...values) => values.find((value) => value !== undefined && value !== null && String(value).trim() !== '');

const normalizeAlign = (value = 'left') => {
  const normalized = String(value || '').toLowerCase();
  if (['center', 'right', 'left'].includes(normalized)) return normalized;
  return 'left';
};

const alignmentStyles = (align) => {
  const normalized = normalizeAlign(align);
  if (normalized === 'center') return { textAlign: 'center', alignItems: 'center', justifyContent: 'center' };
  if (normalized === 'right') return { textAlign: 'right', alignItems: 'flex-end', justifyContent: 'flex-end' };
  return { textAlign: 'left', alignItems: 'flex-start', justifyContent: 'flex-start' };
};

const WaasStoryButton = ({ action, section, theme }) => {
  if (!action?.label) return null;
  const color = action.color || section.style?.accentColor || theme.accent;
  const isOutline = ['outline', 'ghost', 'secondary'].includes(action.variant);
  return (
    <a
      href={action.url || '#'}
      target={action.newTab ? '_blank' : undefined}
      rel="noreferrer noopener"
      style={{
        display: 'inline-flex',
        minHeight: action.height || section.content?.buttonHeight || theme.buttonHeight,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: action.radius || section.content?.buttonRadius || theme.buttonRadius,
        padding: action.padding || '0 24px',
        border: `1px solid ${isOutline ? 'rgba(11, 15, 22, .28)' : color}`,
        background: isOutline ? 'rgba(255, 255, 255, .36)' : color,
        color: action.textColor || (isOutline ? color : '#ffffff'),
        textDecoration: 'none',
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: '.04em',
        textTransform: 'uppercase'
      }}
    >
      {action.label}
    </a>
  );
};

const WaasStorySplitSection = ({ section, theme }) => {
  const t = themeOf(theme);
  const c = section.content || {};
  const style = section.style || {};
  const layout = section.layout || {};
  const blocks = getBlocks(section);
  const textBlocks = blocks.filter((item) => !getImageUrl(item)).slice(0, 2);
  const imageBlocks = blocks.filter((item) => getImageUrl(item));
  const contentAlign = normalizeAlign(pick(c.alignment, c.contentAlignment, style.textAlign, layout.align));
  const align = alignmentStyles(contentAlign);
  const sectionMinHeight = pick(c.sectionHeight, c.height, style.minHeight, style.height);
  const cardMinHeight = pick(c.cardHeight, c.storyHeight, c.innerHeight, style.cardHeight, '370px');
  const containerWidth = pick(c.sectionWidth, c.width, c.storyWidth, style.maxWidth, layout.maxWidth);
  const cardBackground = pick(c.cardBackground, c.storyBackground, c.panelBackground, style.cardBackground, t.surface, '#ffffff');
  const sectionBackground = pick(c.backgroundColor, style.backgroundColor, t.background);
  const gridColumns = pick(c.gridColumns, style.gridTemplateColumns, layout.columns === '1' ? '1fr' : undefined, '1.02fr 1.48fr');
  const imageGridColumns = pick(c.imageGridColumns, '1fr 1fr');
  const imageGridRows = pick(c.imageGridRows, '1fr 1.35fr');
  const actions = getActions(section);
  const finalActions = actions.length ? actions : [{ label: c.primaryActionLabel, url: c.primaryActionUrl, variant: 'outline' }].filter((item) => item.label);

  const containerMaxWidth = layout.width === 'full'
    ? 'none'
    : layout.width === 'narrow'
      ? containerWidth || '920px'
      : containerWidth || t.pageWidth;

  return (
    <section
      data-waas-story-split="true"
      style={{
        backgroundColor: sectionBackground,
        color: style.color || t.color,
        padding: style.padding || t.sectionSpacing || '72px 24px',
        minHeight: sectionMinHeight,
        height: style.height && String(style.height).includes('px') ? style.height : undefined,
        display: 'grid',
        alignItems: pick(c.verticalAlignment, style.alignItems, 'center'),
        overflow: 'hidden'
      }}
    >
      <div
        style={{
          maxWidth: containerMaxWidth,
          width: '100%',
          margin: layout.width === 'full' ? '0' : '0 auto',
          marginTop: style.marginTop || 0,
          background: cardBackground,
          borderRadius: pick(c.cardRadius, style.cardRadius, '24px'),
          padding: pick(c.cardPadding, style.cardPadding, '14px'),
          boxShadow: pick(c.cardShadow, style.cardShadow, t.cardShadow),
          position: 'relative',
          zIndex: 3
        }}
      >
        <div
          className="waas-template-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: gridColumns,
            gap: pick(c.gap, style.gap, '14px'),
            alignItems: pick(c.verticalAlignment, style.alignItems, 'stretch')
          }}
        >
          <div
            style={{
              padding: pick(c.textPadding, '48px 36px 40px'),
              minHeight: cardMinHeight,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: pick(c.textVerticalAlignment, 'space-between'),
              ...align
            }}
          >
            <div>
              {c.eyebrow && <p style={{ margin: '0 0 18px', color: style.accentColor || t.primary, fontSize: 12, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase' }}>{c.eyebrow}</p>}
              <h2 style={{ margin: 0, fontFamily: t.headingFontFamily, fontWeight: t.headingWeight || 300, color: t.accent, fontSize: pick(c.titleSize, 'clamp(44px, 5.2vw, 68px)'), lineHeight: .96 }}>{c.title}</h2>
            </div>
            <div style={{ maxWidth: pick(c.copyWidth, '440px') }}>
              {c.subtitle && <p style={{ margin: '0 0 14px', color: style.color || t.color, fontSize: 15, lineHeight: 1.45 }}>{c.subtitle}</p>}
              {c.body && <p style={{ margin: '0 0 24px', color: t.mutedColor, fontSize: 13, lineHeight: 1.58 }}>{c.body}</p>}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, ...alignmentStyles(contentAlign) }}>
                {finalActions.map((action, index) => <WaasStoryButton key={`${action.label || 'action'}-${index}`} action={action} section={section} theme={t} />)}
              </div>
            </div>
          </div>
          <div className="waas-template-about-tile-grid" style={{ display: 'grid', gridTemplateColumns: imageGridColumns, gridTemplateRows: imageGridRows, gap: pick(c.tileGap, 12), minHeight: cardMinHeight }}>
            {[...(textBlocks.length ? textBlocks : blocks.slice(0, 2)), ...imageBlocks].slice(0, 4).map((item, index) => {
              const imageUrl = getImageUrl(item);
              const imageCard = Boolean(imageUrl);
              return (
                <article key={`${item.title}-${index}`} style={{ minHeight: index > 1 ? pick(c.largeTileHeight, '210px') : pick(c.smallTileHeight, '160px'), borderRadius: pick(c.tileRadius, 16), overflow: 'hidden', background: imageCard ? '#d6dce0' : pick(c.tileBackground, '#f9f9f8'), padding: imageCard ? 0 : 22, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  {imageCard ? (
                    <img src={imageUrl} alt={item.title || c.imageAlt || ''} style={{ width: '100%', height: '100%', objectFit: pick(c.imageFit, 'cover'), display: 'block' }} />
                  ) : (
                    <>
                      <h3 style={{ margin: 0, color: t.accent, fontSize: 30, lineHeight: 1, fontWeight: 400 }}>{item.title}</h3>
                      <p style={{ margin: 'auto 0 0', color: t.mutedColor, fontSize: 13, lineHeight: 1.5 }}>{item.description}</p>
                    </>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WaasStorySplitSection;
